import {
  scrollToElement,
  removeUnitFromValue,
} from "../../../js/utils/helper/helper";
import { EVENTS } from "../../../js/constants";

class SubNavigation {
  /**
   * @param {HTMLElement} element - the subnavigation element
   */
  constructor(element) {
    this.element = element;

    this.config = {
      selectors: {
        buttonSelector: ".js-SubNavigation-button",
        buttonTemplateSelector: ".js-SubNavigation-buttonTemplate",
        contentSelector: ".js-SubNavigation-content",
        linksSelector: ".js-SubNavigation-link",
        listSelector: ".js-SubNavigation-list",
      },
      classes: {
        open: "is-open",
      },
      customProperties: {
        transition: "--default-transition-time",
      },
    };

    this.links = [
      ...element.querySelectorAll(this.config.selectors.linksSelector),
    ];
    this.list = this.element.querySelector(this.config.selectors.listSelector);
    this.content = this.element.querySelector(
      this.config.selectors.contentSelector
    );

    this._onClick = this._onClick.bind(this);
    this._init();
  }

  /**
   * init
   *
   * @private
   */
  _init() {
    window.addEventListener(
      EVENTS.VIEWPORT_CHANGED,
      ({ detail }) => {
        if (detail.viewport === "M" || detail.viewport === "S") {
          this._initButton();
          this._setListHeight();
          this._toggleFocusOnLinks({ enableFocus: false });
          this._bind();
        }
      },
      { once: true }
    );
  }

  /**
   * init button by replacing the button template with the content of the template
   * since there is no sense to have the button on the page if js is disabled
   *
   * @private
   */
  _initButton() {
    const template = this.element.querySelector(
      this.config.selectors.buttonTemplateSelector
    );
    const clone = template.content.firstElementChild.cloneNode(true);
    this.element.replaceChild(clone, template);
  }

  /**
   * binds events
   *
   * @private
   */
  _bind() {
    this.button = this.element.querySelector(
      this.config.selectors.buttonSelector
    );

    this.button.addEventListener("click", this._onClick);
  }

  /**
   * handle mouse clicks
   *
   * @private
   */
  _onClick() {
    const activeButton = this.element.querySelector(`[aria-expanded="true"]`);

    if (activeButton) {
      this._closeList();
    } else {
      this._openList();
      this._scrollToButton();
    }
  }

  /**
   * scrolls to the button
   *
   * @private
   */
  _scrollToButton() {
    const transition = getComputedStyle(
      document.documentElement
    ).getPropertyValue(this.config.customProperties.transition);

    const timeout = removeUnitFromValue(transition);
    scrollToElement(this.button, "smooth", timeout);
  }

  /**
   * opens list
   *
   * @private
   */
  _openList() {
    this.button.setAttribute("aria-expanded", "true");
    this.element.classList.add(this.config.classes.open);
    this._toggleFocusOnLinks({ enableFocus: true });
  }

  /**
   * closes list
   *
   * @private
   */
  _closeList() {
    this.button.setAttribute("aria-expanded", "false");
    this.list.blur(); // to remove focus styles
    this._toggleFocusOnLinks({ enableFocus: false });
    this.element.classList.remove(this.config.classes.open);
  }

  /**
   * sets the `--SubNavigation-content-height` custom property for `content` element
   * to the same size as the height of the `list` element,
   * to be able to animate content via `transition: height` in css
   *
   * @private
   */
  _setListHeight() {
    const listHeight = this.list.getBoundingClientRect().height;
    this.content.style.setProperty(
      "--SubNavigation-content-height",
      `${listHeight / 10}rem`
    );
  }

  /**
   * enable/disable focus on links
   * to prevent links get focused in the hidden elements (with `Tab` keyboard key)
   * by toggling tabindex=-1 on all links inside the sibling of the given element
   *
   * @param {boolean} enableFocus - state
   * @private
   */
  _toggleFocusOnLinks({ enableFocus }) {
    this.links.forEach((link) => {
      if (enableFocus) {
        link.removeAttribute("tabindex");
      } else {
        link.setAttribute("tabindex", "-1");
      }
    });
  }
}

export default SubNavigation;
